import React, { useEffect, useState } from 'react';
import type { ChatSkillDto } from '../../types/api';

export interface ChatSkillSelectorProps {
  skills: ChatSkillDto[];
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  disabled?: boolean;
}

function skillIcon(skill: ChatSkillDto): string {
  const id = skill.skillId.toLowerCase();
  const name = skill.displayName.toLowerCase();
  if (id.includes('xlsx') || name.includes('excel')) return 'table_view';
  if (id.includes('pptx') || name.includes('powerpoint')) return 'slideshow';
  if (id.includes('pdf') || name === 'pdf') return 'picture_as_pdf';
  if (id.includes('docx') || name.includes('word')) return 'article';
  if (name.includes('construction') || name.includes('plan')) return 'architecture';
  return 'auto_awesome';
}

const ChatSkillSelector: React.FC<ChatSkillSelectorProps> = ({
  skills,
  selectedIds,
  onChange,
  disabled = false,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftIds, setDraftIds] = useState<string[]>(selectedIds);

  useEffect(() => {
    if (!drawerOpen) setDraftIds(selectedIds);
  }, [selectedIds, drawerOpen]);

  if (skills.length === 0) return null;

  const openDrawer = () => {
    if (disabled) return;
    setDraftIds(selectedIds);
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const toggleDraft = (skillId: string) => {
    setDraftIds((prev) =>
      prev.includes(skillId) ? prev.filter((id) => id !== skillId) : [...prev, skillId]
    );
  };

  const applySelection = () => {
    onChange(draftIds);
    closeDrawer();
  };

  const selectedCount = selectedIds.length;
  const hasSelection = selectedCount > 0;
  const buttonLabel =
    selectedCount === 0
      ? 'Skills'
      : `${selectedCount} ${selectedCount === 1 ? 'Skill' : 'Skills'} selected`;

  return (
    <>
      <div className="flex items-center gap-2 shrink-0" role="group" aria-label="AI skills">
        <button
          type="button"
          onClick={openDrawer}
          disabled={disabled}
          aria-label={
            hasSelection
              ? `${selectedCount} ${selectedCount === 1 ? 'skill' : 'skills'} selected`
              : 'Select AI skills'
          }
          title={hasSelection ? buttonLabel : 'Select AI skills'}
          className={`px-3 py-1.5 rounded-lg border text-[10px] font-bold tracking-widest uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            hasSelection
              ? 'bg-primary/20 border-primary text-primary'
              : 'bg-slate-900 border-slate-700 text-on-surface-variant hover:bg-slate-800 hover:text-on-surface'
          }`}
        >
          {buttonLabel}
        </button>
      </div>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm"
          role="presentation"
          onClick={closeDrawer}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Select AI skills"
            className={`fixed bottom-0 left-0 w-full bg-surface border-t border-slate-800 rounded-t-2xl transition-transform duration-300 ${
              drawerOpen ? 'translate-y-0' : 'translate-y-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 max-w-lg mx-auto max-h-[min(85vh,640px)] overflow-y-auto scrollbar-hide">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-black tracking-tight text-white uppercase">
                  Please select available skills
                </h3>
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="p-2 hover:bg-surface-container-high rounded-full text-on-surface-variant"
                  aria-label="Close skills drawer"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <section className="space-y-3">
                {skills.map((skill) => {
                  const checked = draftIds.includes(skill.skillId);
                  return (
                    <label
                      key={skill.skillId}
                      className={`flex items-start gap-4 p-4 bg-surface-container-high border rounded-xl cursor-pointer transition-colors group ${
                        checked
                          ? 'border-primary/50'
                          : 'border-outline hover:border-primary/50'
                      }`}
                    >
                      <div className="p-2 bg-primary/10 rounded-lg text-primary shrink-0">
                        <span className="material-symbols-outlined">{skillIcon(skill)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center gap-2">
                          <h4 className="text-sm font-bold text-on-surface">{skill.displayName}</h4>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleDraft(skill.skillId)}
                            className="rounded border-outline-variant bg-surface-container-lowest text-primary focus:ring-primary shrink-0"
                          />
                        </div>
                        {skill.description && (
                          <p className="text-xs text-on-surface-variant mt-1">{skill.description}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </section>

              <button
                type="button"
                onClick={applySelection}
                className="w-full mt-8 py-3 bg-primary text-on-primary font-bold uppercase tracking-widest text-xs rounded-xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-transform"
              >
                Confirm selection
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatSkillSelector;
